import { createSelector } from 'reselect'

import {
  DeliveryIssue,
  DeliveryIssueListItem,
  DeliveryIssueStatus,
} from '../models/types'
import { RootState } from '../reducers'
import { DasherCountries } from '../components/organisms/delivery-issues/types'

export const getDasherId = (state: RootState): string => state.dasherId

/**
 * Get the details for the current delivery issue from Redux store
 */
export const deliveryIssueDetailsSelector = (state: RootState): DeliveryIssue =>
  state.deliveryIssueDetails

/**
 * Get the list of delivery issues for the current Dasher from Redux store
 */
export const deliveryIssuesSelector = (
  state: RootState
): DeliveryIssueListItem[] => state.deliveryIssuesList.entities

/**
 * Get a list of critical delivery issues.
 * Critical issues are issues defined as having a priority of 1 and a status of
 * 'unread'. Filter out critical delivery issues from a list of delivery
 * issues.
 * @param state redux state
 */
export const getCriticalDeliveryIssues: (
  state: RootState
) => DeliveryIssueListItem[] = createSelector(
  deliveryIssuesSelector,
  (deliveryIssues: DeliveryIssueListItem[]): DeliveryIssueListItem[] =>
    deliveryIssues.filter(deliveryIssue => {
      return (
        deliveryIssue.priority === 1 &&
        deliveryIssue.status === DeliveryIssueStatus.Unread
      )
    })
)

/**
 * Get whether a given delivery issue already has a dispute submitted. Given
 * that we infer a delivery issue status can only be one of the
 * DeliveryIssueStatus enum values, the assumption is that if the issue is not
 * Unread or Read then it has to be one of the other statuses which mean it is
 * in some state of having a dispute submitted.
 */
export const hasSubmittedDisputeSelector: (
  state: RootState
) => boolean = createSelector(
  deliveryIssueDetailsSelector,
  ({ status }: DeliveryIssue): boolean =>
    status !== DeliveryIssueStatus.Unread && status !== DeliveryIssueStatus.Read
)

/**
 * Returns whether or not the app is fetching the delivery issues list.
 * @param state Redux store
 */
export const isLoadingDeliveryIssuesList = (state: RootState): boolean =>
  state.deliveryIssuesList.isLoading

/**
 * Returns the country code (eg, 'US', 'AU', 'CA') associated with dasher
 * phone number.
 *
 * @param {RootState} state
 * @returns {DasherCountries}
 */
export const getDasherCountry = (state: RootState): DasherCountries =>
  state.country
